import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent, CardActions,
  Button, Chip, CircularProgress, Alert, Divider, List, ListItem,
  ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, IconButton, Tabs, Tab,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DownloadIcon from '@mui/icons-material/Download';
import GavelIcon from '@mui/icons-material/Gavel';
import {
  getDealRoom, requestDealRoomAccess, checkDealRoomAccess,
  listDocumentsByStartup, downloadDocument,
} from '../services/api';

const DealRoom = () => {
  const { startupId } = useParams();
  const [dealRoom, setDealRoom] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ndaOpen, setNdaOpen] = useState(false);
  const [activeFolder, setActiveFolder] = useState('all');
  const [requesting, setRequesting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [drRes, accessRes] = await Promise.all([
        getDealRoom(startupId),
        checkDealRoomAccess(startupId),
      ]);
      setDealRoom(drRes.data);
      setAccess(accessRes.data);

      if (accessRes.data?.hasAccess) {
        const docsRes = await listDocumentsByStartup(startupId);
        setDocuments(docsRes.data?.documents || docsRes.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load deal room');
    } finally {
      setLoading(false);
    }
  }, [startupId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRequestAccess = async () => {
    setRequesting(true);
    try {
      await requestDealRoomAccess(startupId);
      setNdaOpen(false);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Access request failed');
    } finally {
      setRequesting(false);
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const res = await downloadDocument(docId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Download failed');
    }
  };

  const folders = [...new Set(documents.map((d) => d.folder || 'General'))];
  const filteredDocs = activeFolder === 'all'
    ? documents
    : documents.filter((d) => (d.folder || 'General') === activeFolder);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            {dealRoom?.startup?.name || 'Deal Room'}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, mt: 1 }}>
            {dealRoom?.startup?.tagline || 'Confidential deal materials'}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Chip
              icon={access?.hasAccess ? <LockOpenIcon /> : <LockIcon />}
              label={access?.hasAccess ? 'Access Granted' : 'Restricted'}
              sx={{ color: 'white', borderColor: 'white' }}
              variant="outlined"
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        {!access?.hasAccess ? (
          /* NDA / Access Request */
          <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <LockIcon sx={{ fontSize: 64, color: '#478289', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Access Required
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                You need to sign an NDA and request access to view the deal room materials for this startup.
              </Typography>
              {access?.status === 'pending' ? (
                <Chip label="Access Request Pending" color="warning" />
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<GavelIcon />}
                  onClick={() => setNdaOpen(true)}
                  sx={{ backgroundColor: '#478289', '&:hover': { backgroundColor: '#3a6b70' } }}
                >
                  Sign NDA & Request Access
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Documents View */
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={activeFolder}
                onChange={(_, v) => setActiveFolder(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ '& .Mui-selected': { color: '#478289' }, '& .MuiTabs-indicator': { backgroundColor: '#478289' } }}
              >
                <Tab label="All Documents" value="all" />
                {folders.map((f) => (
                  <Tab key={f} label={f} value={f} icon={<FolderIcon />} iconPosition="start" />
                ))}
              </Tabs>
            </Box>

            <Grid container spacing={3}>
              {filteredDocs.length === 0 ? (
                <Grid item xs={12}>
                  <Typography color="textSecondary" sx={{ textAlign: 'center', py: 6 }}>
                    No documents available yet.
                  </Typography>
                </Grid>
              ) : (
                filteredDocs.map((doc) => (
                  <Grid item xs={12} sm={6} md={4} key={doc.id}>
                    <Card sx={{ height: '100%', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <InsertDriveFileIcon sx={{ color: '#478289', mr: 1 }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                            {doc.name || doc.filename}
                          </Typography>
                        </Box>
                        {doc.folder && <Chip label={doc.folder} size="small" sx={{ mb: 1 }} />}
                        <Typography variant="body2" color="textSecondary">
                          {doc.description || ''}
                        </Typography>
                        {doc.uploadedAt && (
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                            Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownload(doc.id, doc.name || doc.filename)}
                        >
                          Download
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          </>
        )}
      </Container>

      {/* NDA Dialog */}
      <Dialog open={ndaOpen} onClose={() => setNdaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Non-Disclosure Agreement</DialogTitle>
        <DialogContent>
          <DialogContentText>
            By signing this NDA, you agree to keep all deal room materials strictly confidential.
            You may not share, copy, or distribute any information without written consent.
          </DialogContentText>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="textSecondary">
            This NDA is legally binding. By clicking "Sign & Request Access" you acknowledge
            you have read and agree to the terms.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNdaOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRequestAccess}
            disabled={requesting}
            sx={{ backgroundColor: '#478289' }}
          >
            {requesting ? <CircularProgress size={20} /> : 'Sign & Request Access'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DealRoom;
